import { useQueryClient } from "@tanstack/react-query";
import PageAPI from "../../../../api/api/PageAPI";
import { TreeItem } from "../../types";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../../atoms/WorkspaceAtoms";
import PopoverDropdownMenu from "../../../Menus/PopoverDropdownMenu";
import DropdownOptionWithIcon from "../../../Menus/DropdownOptionWithIcon";
import useCloudPermissions from "../../../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../../../hooks/useWorkspacePermissions";
import DropdownMenuDivider from "../../../Menus/DropdownMenuDivider";
import useDuplicatePage from "../../../../api/api-hooks/pages/useDuplicatePage";
import useDuplicateFolder from "../../../../api/api-hooks/folders/useDuplicateFolder";
import { AppConfirm } from "../../../../utils/PopupUtil";
import useDeleteItem from "../../../../api/api-hooks/workspaces/sidebar/useDeleteItem";
import useUnpublishCloudApp from "../../../../api/api-hooks/cloudApps/useUnpublishCloudApp";
import { Page } from "../../../../types/GlobalInterfaces";
import { useParams } from "react-router-dom";
import {
  getUserPagePermissions,
  userHasScope,
} from "../../../../utils/UserUtils";
import useUser from "../../../../api/api-hooks/auth/useUser";
import { CloudAppDrawerType } from "../../../Drawers/CloudAppDrawer";

export interface CustomLinkOptionsProps {
  setEditingItem: React.Dispatch<React.SetStateAction<TreeItem | null>>;
  setAddPage: React.Dispatch<React.SetStateAction<string>>;
  setSelectedPage: React.Dispatch<
    React.SetStateAction<
      Page | { __Loading: boolean } | { __NotFound: true } | null
    >
  >;
  setCloudAppDrawerOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setCloudAppDrawerType: React.Dispatch<
    React.SetStateAction<CloudAppDrawerType>
  >;
  setCloudAppFolder: React.Dispatch<React.SetStateAction<any>>;
}

export default function CustomLinkOptions({
  item,
  setEditingItem,
  setAddPage,
  setSelectedPage,
  setCloudAppDrawerOpened,
  setCloudAppDrawerType,
  setCloudAppFolder,
}: {
  item: TreeItem;
} & CustomLinkOptionsProps) {
  const client = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);
  const data = item.data;

  const { tenantID } = useParams();
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();

  const { user, pPermissions } = useUser();

  const { duplicatePage } = useDuplicatePage();
  const { duplicateFolder } = useDuplicateFolder();
  const { deleteItem } = useDeleteItem();
  const { unpublishCloudApp } = useUnpublishCloudApp();

  if (!data) return null;

  const hasChildren = !!item.children?.length;
  const pageID = "PageID" in data ? data.PageID : null;
  const pagePermissions = getPagePermissions();
  const isGlobal = "GlobalAppID" in data ? !!data.GlobalAppID : false;

  return (
    <PopoverDropdownMenu>
      {!hasChildren && (
        <>
          <DropdownOptionWithIcon
            name={`Open In New Tab`}
            onClick={openInNewTab}
            icon="mdi mdi-open-in-new"
          />
          {data.Type === "PageLink" && !isGlobal && (
            <DropdownOptionWithIcon
              name="Duplicate Page"
              onClick={() => duplicatePage(data.PageID)}
              icon="mdi mdi-content-copy"
              disabled={!pagePermissions.canDuplicate}
            />
          )}
        </>
      )}
      {hasChildren && (
        <>
          <DropdownOptionWithIcon
            name="Add Page To Folder"
            icon="mdi mdi-file-table-box-outline"
            onClick={() => setAddPage(item.id)}
          />
          <DropdownOptionWithIcon
            name="Rename Folder"
            onClick={startRenaming}
            icon="mdi mdi-pencil-outline"
            disabled={
              !cPermissions.cloud_duplicate_folder ||
              !wPermissions.workspace_duplicate_folder
            }
          />
          <DropdownMenuDivider />
          <DropdownOptionWithIcon
            name="Duplicate Folder"
            onClick={() => duplicateFolder(item.id)}
            icon="mdi mdi-content-copy"
            disabled={
              !cPermissions.cloud_duplicate_folder ||
              !wPermissions.workspace_duplicate_folder
            }
          />
          <DropdownOptionWithIcon
            name="Delete Folder"
            onClick={() => startDeleting(item, deleteItem, unpublishCloudApp)}
            icon="mdi mdi-delete-outline"
            disabled={
              !cPermissions.cloud_duplicate_folder ||
              !wPermissions.workspace_duplicate_folder
            }
          />
          <DropdownMenuDivider />
          <DropdownOptionWithIcon
            name="Convert to App"
            onClick={() => handleCreateNewAppFromFolder(item)}
            icon="mdi mdi-shape-square-plus"
          />
        </>
      )}
      {data.Type === "External" && (
        <DropdownOptionWithIcon
          name="Delete Link"
          onClick={() => startDeleting(item, deleteItem, unpublishCloudApp)}
          icon="mdi mdi-delete-outline"
          disabled={!pagePermissions.canDelete}
        />
      )}
      {pageID && (
        <DropdownOptionWithIcon
          name="Manage Page"
          onClick={openPageSettings}
          icon="mdi mdi-cog-outline"
        />
      )}
    </PopoverDropdownMenu>
  );

  function getPagePermissions() {
    const permissions = getUserPagePermissions(
      user,
      tenantID,
      pageID,
      pPermissions
    );

    return {
      canRename:
        cPermissions.cloud_modify_page_settings ||
        wPermissions.workspace_modify_page_settings ||
        userHasScope(permissions, "page_modify_page_settings"),
      canDuplicate:
        cPermissions.cloud_duplicate_page ||
        wPermissions.workspace_duplicate_page ||
        userHasScope(permissions, "page_create_duplicate_page"),
      canDelete:
        cPermissions.cloud_delete_page ||
        wPermissions.workspace_delete_page ||
        userHasScope(permissions, "page_delete_page"),
    };
  }

  function getUrl() {
    if (!data) return null;

    let url = "Link" in data ? data.Link : "";

    if (data.Type === "ModuleLink") {
      url = `/clouds/${tenantID}/records/${data.ModuleAPIName}`;
    }
    if (data.Type === "PageLink") {
      url = `/clouds/${tenantID}/workspaces/${data.WorkspaceID}/pages/${data.PageID}/views/None`;
    }

    return url;
  }

  function openInNewTab() {
    const url = getUrl();

    const newWindow = window.open(`${url}`, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  }

  async function openPageSettings() {
    if (!pageID) return;

    let page = client.getQueryData<Page>(["page", pageID]);

    if (page) return setSelectedPage(page);

    setSelectedPage({ __Loading: true });

    try {
      page = await PageAPI.getPageByID(workspaceID, pageID).then(
        (data) => data?.data?.data?.page
      );
    } catch {}

    page && data && (page.isApp = "isApp" in data ? data.isApp : false);
    client.setQueryData(["page", pageID], page);

    if (!page) return setSelectedPage({ __NotFound: true, id: pageID });

    setSelectedPage(page);
  }

  function handleCreateNewAppFromFolder(folder: any) {
    setCloudAppDrawerOpened(true);
    setCloudAppDrawerType("CreateFromFolder");
    setCloudAppFolder(folder);
  }

  // Starts the rename process
  function startRenaming() {
    setEditingItem(item);
  }
}

// Starts the deletion process
export function startDeleting(
  item: TreeItem,
  deleteItem: any,
  unpublishCloudApp: any
) {
  const data = item.data;

  if (!data) return;

  const isFolder = !!item?.children?.length && data.Type === "Group";
  const isPage = data.Type === "PageLink" && !!data?.PageID;
  const isApp = data.Type === "CloudAppOwner";
  let isLink = false;

  let title, html, confirmButtonText;

  if (isFolder) {
    title = "Delete Folder?";
    html =
      "Are you sure you want to delete this folder? <br/> All items inside will be removed. Any pages will be deleted. <br/> This action cannot be undone.";
  } else if (isPage) {
    title = "Delete Page?";
    html =
      "Are you sure you want to delete this page? <br/> This action cannot be undone.";
  } else if (isApp) {
    title = "Unpublish App?";
    confirmButtonText = "Unpublish";
    html =
      "Are you sure you want to unpublish this app? <br/> This app will be removed from all workspaces that have it installed. <br/> This app will stay as a folder.";
  } else {
    isLink = true;
    title = "Delete Link?";
    html = "Are you sure you want to delete this link?";
  }

  AppConfirm({
    title: title,
    html: html,
    confirmButtonText: confirmButtonText,
  }).then((result) => {
    if (result.isConfirmed && (isFolder || isPage || isLink)) {
      deleteItem(item.id);
    } else if (result.isConfirmed && isApp) {
      unpublishCloudApp(item.id);
    }
  });
}
